/**
 * WHEN A PICKER ADOPTS A VALUE THAT CHANGED UNDER IT (QA re-verification,
 * 2026-09-22, N-02).
 *
 * `EntityPicker` shows the stored value's name in its own text box. When the
 * selection changes upstream — a header company repointing its lines, or a
 * dialog seeding its fields from the row it was just opened on — the box has to
 * follow. The one thing it must not do is overwrite what somebody is part way
 * through typing.
 *
 * FOCUS IS NOT TYPING, and conflating the two is the defect this exists to
 * close. The guard used to stand down whenever the field held focus. Every
 * Fluent dialog moves focus to its first focusable control as it opens, and on
 * `Rules → Codes → Edit Rule` that control is the vendor picker — so the picker
 * was always "being edited" by the time the dialog's own seeding effect ran one
 * render later, and it refused the seed. The box therefore showed the
 * PREVIOUSLY opened rule's vendor, and nothing at all on the first open of a
 * session. The Account and Department pickers in the same dialog were right,
 * because focus only ever lands on one of them.
 *
 * Its own module so a test can call it: `app/vitest.config.ts` runs
 * `environment: 'node'`, there is no DOM here, and this decision cannot be
 * asserted through a mounted component. Same reason `saveOnBlur.ts` and
 * `handlingTimer.ts` were split out of their components.
 */

interface PickerResyncState {
  /** The field holds focus. */
  editing: boolean
  /** Text was typed into the box and not yet committed to a pick. */
  typedWithoutSelect: boolean
  /** The display name of the value the caller now holds — `''` when cleared. */
  valueName: string
  /** A pick has just landed and its composed display name is owed to the box. */
  adoptNextValue: boolean
}

/**
 * Whether the box should be reset to `valueName`.
 *
 * It holds back in exactly one case: the reader has focus AND has typed
 * something they have not committed AND there is still a value to go back to.
 * Everything else adopts —
 *
 *  * not focused: an ordinary upstream change, and the box must show what is
 *    stored;
 *  * focused but nothing typed: the dialog's own seeding, or a value repointed
 *    elsewhere. There is no edit in progress to protect;
 *  * the value was CLEARED: a field whose value is gone while its old words are
 *    still in the box reads as a pair that is still set (owner, 2026-08-10);
 *  * a pick just landed: the caller may compose a richer display name than the
 *    option carried (`A — Newspaper ad` against a bare `A`), and that name
 *    arrives through this path.
 */
export function shouldAdoptUpstreamValue(state: PickerResyncState): boolean {
  if (state.valueName === '') return true
  if (state.adoptNextValue) return true
  if (!state.editing) return true
  return !state.typedWithoutSelect
}
